/**
 * 文件上传下载工具类
 * 使用项目统一的 http.js 进行请求
 */
import http from "@/api/http";
import { Message } from "element-ui";
import {
  getBrowserFingerprint,
  getFingerDevice,
  getLoginFingerprint,
} from "@/utils/fingerprint";

class FileHelper {
  /**
   * 单文件上传
   * @param {Object} options - 上传配置
   * @param {File} options.file - 要上传的文件
   * @param {string} options.url - 上传接口地址
   * @param {Object} [options.headers] - 请求头
   * @param {Object} [options.params] - URL参数
   * @param {Function} [options.onProgress] - 进度回调 (progress: number) => void
   * @param {string} [options.fieldName='file'] - 文件字段名
   * @returns {Promise} - 返回上传结果
   */
  static async uploadFile(options) {
    const {
      file,
      url,
      headers = {},
      params = {},
      onProgress,
      fieldName = "file",
    } = options;

    const formData = new FormData();
    formData.append(fieldName, file);

    Object.entries(params).forEach(([key, value]) => {
      // 如果是数组，使用 append 多次添加
      if (Array.isArray(value)) {
        value.forEach((item) => {
          formData.append(key, item);
        });
      } else {
        formData.append(key, value);
      }
    });

    try {
      const storedFingerprint = getLoginFingerprint();
      const [fingerprint, fingerDevice] = await Promise.all([
        storedFingerprint ? Promise.resolve(storedFingerprint) : getBrowserFingerprint(),
        getFingerDevice(),
      ]);
      const response = await http.post(url, formData, {
        headers: {
          ...headers,
          fingerprint,
          "Finger-Device": fingerDevice,
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total > 0) {
            const progress = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100,
            );
            onProgress(progress);
          }
        },
      });

      return response;
    } catch (error) {
      console.error("文件上传失败:", error);
      throw error;
    }
  }

  /**
   * 多文件上传
   * @param {Object} options - 上传配置
   * @param {File[]} options.files - 要上传的文件数组
   * @param {string} options.url - 上传接口地址
   * @param {Object} [options.headers] - 请求头
   * @param {Object} [options.params] - URL参数
   * @param {Function} [options.onProgress] - 进度回调 (progress: number) => void
   * @param {string} [options.fieldName='files'] - 文件字段名
   * @param {boolean} [options.sequential=false] - 是否顺序上传
   * @returns {Promise} - 返回上传结果数组
   */
  static async uploadFiles(options) {
    const {
      files,
      url,
      headers = {},
      params = {},
      onProgress,
      fieldName = "files",
      sequential = false,
    } = options;

    if (sequential) {
      const results = [];
      let totalProgress = 0;
      const progressIncrement = 100 / files.length;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append(fieldName, file);

        try {
          const response = await http.post(url, formData, {
            params,
            headers: {
              ...headers,
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (progressEvent) => {
              if (onProgress && progressEvent.total > 0) {
                const fileProgress = Math.round(
                  (progressEvent.loaded / progressEvent.total) *
                    progressIncrement,
                );
                const currentProgress = totalProgress + fileProgress;
                onProgress(Math.min(currentProgress, 100));
              }
            },
          });

          results.push(response);
          totalProgress += progressIncrement;
        } catch (error) {
          console.error(`文件 ${file.name} 上传失败:`, error);
          results.push({
            success: false,
            error: error.message,
            fileName: file.name,
          });
        }
      }

      if (onProgress) {
        onProgress(100);
      }

      return results;
    } else {
      const promises = files.map((file) => {
        const formData = new FormData();
        formData.append(fieldName, file);

        return http
          .post(url, formData, {
            params,
            headers: {
              ...headers,
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response) => ({
            success: true,
            data: response,
            fileName: file.name,
          }))
          .catch((error) => ({
            success: false,
            error: error.message,
            fileName: file.name,
          }));
      });

      const results = await Promise.all(promises);

      if (onProgress) {
        onProgress(100);
      }

      return results;
    }
  }

  /**
   * 通过URL下载文件
   * @param {Object} options - 下载配置
   * @param {string} options.url - 文件URL
   * @param {string} [options.fileName] - 文件名（可选）
   * @param {Object} [options.headers] - 请求头
   * @param {boolean} [options.newWindow=false] - 是否在新窗口打开
   */
  static downloadByUrl(options) {
    const { url, fileName, headers = {}, newWindow = true } = options;

    if (newWindow) {
      window.open(url, "_blank");
      return;
    }

    const link = document.createElement("a");
    link.href = url;

    if (fileName) {
      link.download = fileName;
    }

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * 通过接口下载文件（二进制流）
   * @param {Object} options - 下载配置
   * @param {string} options.url - 下载接口地址
   * @param {string} [options.fileName] - 文件名
   * @param {Object} [options.headers] - 请求头
   * @param {Object} [options.params] - 请求参数（POST时放在请求体中）
   * @param {string} [options.method='post'] - 请求方法
   * @param {Function} [options.onProgress] - 进度回调 (progress: number) => void
   * @returns {Promise}
   */
  static async downloadByApi(options) {
    const {
      url,
      fileName,
      headers = {},
      params = {},
      method = "post",
      onProgress,
    } = options;

    try {
      const isGet = method.toLowerCase() === "get";
      console.log("📤 下载请求配置:", { url, method, params, headers }); // 增加日志排查

      // 根据请求方法决定参数传递方式
      const response = await http[method.toLowerCase()](
        url,
        // POST/PUT: 参数放在请求体（data）
        !isGet && Object.keys(params).length > 0 ? params : undefined,
        {
          // GET: 参数放在URL查询参数
          ...(isGet && Object.keys(params).length > 0 ? { params } : {}),
          headers: {
            Accept: "application/octet-stream",
            // POST请求默认JSON格式，可根据后端需求调整
            ...headers,
          },
          responseType: "blob",
          onDownloadProgress: (progressEvent) => {
            if (onProgress && progressEvent.total > 0) {
              const progress = Math.round(
                (progressEvent.loaded / progressEvent.total) * 100,
              );
              onProgress(progress);
            }
          },
          // 解决跨域凭证问题（如果后端需要）
          // withCredentials: true,
          // 延长超时时间，避免大文件下载超时
          timeout: 60000,
        },
      );

      // 从响应头获取文件名
      let finalFileName = fileName;
      if (!finalFileName) {
        const contentDisposition =
          response.headers?.["content-disposition"] ||
          response.headers?.["Content-Disposition"];
        if (contentDisposition) {
          const match = contentDisposition.match(
            /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
          );
          if (match && match[1]) {
            finalFileName = decodeURIComponent(match[1].replace(/['"]/g, ""));
          }
        }
      }

      // 创建下载链接
      const blob = new Blob([response.data]);
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = finalFileName || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      return { success: true, fileName: finalFileName };
    } catch (error) {
      console.error("❌ 文件下载失败详情:", error);
      // 增加详细错误日志
      if (error.response) {
        console.error("响应状态:", error.response.status);
        console.error("响应头:", error.response.headers);
      } else if (error.request) {
        console.error("无响应，请求信息:", error.request);
      } else {
        console.error("请求配置错误:", error.message);
      }

      // 更友好的错误提示
      const errorMsg =
        error.code === "ERR_NETWORK"
          ? "网络连接失败，请检查网络或联系管理员"
          : `文件下载失败: ${error.message || "未知错误"}`;

      throw new Error(errorMsg);
    }
  }

  /**
   * 导出JSON数据为文件
   * @param {Object} options - 导出配置
   * @param {any} options.data - 要导出的数据
   * @param {string} options.fileName - 文件名（不含扩展名）
   * @param {string} [options.type='json'] - 导出类型 (json/csv)
   * @returns {void}
   */
  static exportData(options) {
    const { data, fileName, type = "json" } = options;

    let content = "";
    let extension = ".json";
    let mimeType = "application/json";

    if (type === "csv") {
      extension = ".csv";
      mimeType = "text/csv";

      if (Array.isArray(data)) {
        const headers = Object.keys(data[0] || {});
        content = headers.join(",") + "\n";

        data.forEach((row) => {
          const values = headers.map((header) => {
            const value = row[header];
            if (
              value &&
              typeof value === "string" &&
              (value.includes(",") ||
                value.includes('"') ||
                value.includes("\n"))
            ) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          });
          content += values.join(",") + "\n";
        });
      }
    } else {
      content = typeof data === "string" ? data : JSON.stringify(data, null, 2);
    }

    const blob = new Blob([content], { type: mimeType });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `${fileName}${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  }

  /**
   * 上传并下载（如导入导出场景）
   * @param {Object} options - 配置
   * @param {File} options.file - 上传文件
   * @param {string} options.uploadUrl - 上传接口
   * @param {string} options.downloadUrl - 下载接口
   * @param {Object} [options.headers] - 请求头
   * @param {Function} [options.onUploadProgress] - 上传进度回调
   * @param {Function} [options.onDownloadProgress] - 下载进度回调
   * @returns {Promise}
   */
  static async uploadAndDownload(options) {
    const {
      file,
      uploadUrl,
      downloadUrl,
      headers = {},
      onUploadProgress,
      onDownloadProgress,
    } = options;

    try {
      const uploadResult = await this.uploadFile({
        file,
        url: uploadUrl,
        headers,
        onProgress: onUploadProgress,
      });

      const downloadResult = await this.downloadByApi({
        url: downloadUrl,
        headers,
        onProgress: onDownloadProgress,
      });

      return { uploadResult, downloadResult };
    } catch (error) {
      console.error("上传下载失败:", error);
      throw error;
    }
  }
}

export default FileHelper;

export const uploadFile = FileHelper.uploadFile.bind(FileHelper);
export const uploadFiles = FileHelper.uploadFiles.bind(FileHelper);
export const downloadByUrl = FileHelper.downloadByUrl.bind(FileHelper);
export const downloadByApi = FileHelper.downloadByApi.bind(FileHelper);
export const exportData = FileHelper.exportData.bind(FileHelper);
export const uploadAndDownload = FileHelper.uploadAndDownload.bind(FileHelper);
